// workflowGraphUtils.js
// Workflow template graph: outcomes, edges, migration from prerequisites, validation.

import { MISSION_TASK_HANDLED_BY } from 'missions/taskmodal/missionTaskModalUtils';
import { generateKey } from 'utilities/Keys';

export const WORKFLOW_OUTCOME_COMPLETE = 'complete';

const toKey = (value) => String(value || '').trim();

const toOutcomeKey = (value) => {
  const raw = toKey(value).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  return raw || '';
};

/**
 * Normalizes a custom outcome entry on a people step.
 * Preserves spaces in labels while editing; stable keys prefer an existing key.
 *
 * @param {object|string|null|undefined} value
 * @returns {{ key: string, label: string }|null}
 */
export function normalizeWorkflowOutcome(value) {
  if (typeof value === 'string') {
    const label = String(value);
    const key = toOutcomeKey(label);
    if (!key || key === WORKFLOW_OUTCOME_COMPLETE) return null;
    return { key, label };
  }

  if (!value || typeof value !== 'object') return null;

  // Do not trim labels here — trimming on each keystroke blocks typing spaces.
  const label = String(value.label || value.title || value.name || '');
  const key = toOutcomeKey(value.key) || toOutcomeKey(label);
  if (!key || key === WORKFLOW_OUTCOME_COMPLETE) return null;

  return {
    key,
    label,
  };
}

/**
 * Normalizes custom outcomes for a task (people only).
 *
 * @param {unknown} value
 * @param {object} [options]
 * @param {string} [options.handledBy]
 * @returns {{ key: string, label: string }[]}
 */
export function normalizeWorkflowOutcomes(value, options = {}) {
  const handledBy = String(options.handledBy || '').trim().toLowerCase();
  if (handledBy === MISSION_TASK_HANDLED_BY.BOT) return [];

  const seen = new Set();
  const next = [];
  (Array.isArray(value) ? value : []).forEach((entry) => {
    const outcome = normalizeWorkflowOutcome(entry);
    if (!outcome || seen.has(outcome.key)) return;
    seen.add(outcome.key);
    next.push(outcome);
  });
  return next;
}

/**
 * Normalizes one workflow edge.
 *
 * @param {object|null|undefined} value
 * @returns {{ key: string, fromTaskKey: string, outcomeKey: string, toTaskKey: string|null }|null}
 */
export function normalizeWorkflowEdge(value) {
  if (!value || typeof value !== 'object') return null;

  const fromTaskKey = toKey(value.fromTaskKey || value.from);
  const outcomeKey = toOutcomeKey(value.outcomeKey || value.outcome) || WORKFLOW_OUTCOME_COMPLETE;
  const rawTo = value.toTaskKey ?? value.to;
  const toTaskKey = rawTo == null || rawTo === ''
    ? null
    : toKey(rawTo);

  if (!fromTaskKey) return null;
  if (toTaskKey && toTaskKey === fromTaskKey) return null;

  return {
    key: toKey(value.key) || `edge_${fromTaskKey}_${outcomeKey}_${toTaskKey || 'end'}`,
    fromTaskKey,
    outcomeKey,
    toTaskKey,
  };
}

/**
 * Normalizes workflow edges; keeps one edge per (from, outcome).
 *
 * @param {unknown} value
 * @returns {Array<{ key: string, fromTaskKey: string, outcomeKey: string, toTaskKey: string|null }>}
 */
export function normalizeWorkflowEdges(value) {
  const seen = new Set();
  const next = [];

  (Array.isArray(value) ? value : []).forEach((entry) => {
    const edge = normalizeWorkflowEdge(entry);
    if (!edge) return;
    const dedupeKey = `${edge.fromTaskKey}\u0001${edge.outcomeKey}`;
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    next.push(edge);
  });

  return next;
}

/**
 * Canvas position for a task node.
 *
 * @param {unknown} value
 * @returns {{ x: number, y: number }|null}
 */
export function normalizeWorkflowCanvasPosition(value) {
  if (!value || typeof value !== 'object') return null;
  const x = Number(value.x);
  const y = Number(value.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x, y };
}

/**
 * Outcome keys available on a task (complete + customs for people).
 *
 * @param {object|null|undefined} task
 * @returns {string[]}
 */
export function listWorkflowTaskOutcomeKeys(task) {
  const handledBy = String(task?.handledBy || '').trim().toLowerCase();
  if (handledBy === MISSION_TASK_HANDLED_BY.BOT) {
    return [WORKFLOW_OUTCOME_COMPLETE];
  }

  const customs = normalizeWorkflowOutcomes(task?.outcomes, { handledBy });
  return [WORKFLOW_OUTCOME_COMPLETE, ...customs.map((entry) => entry.key)];
}

/**
 * Builds Complete-spine edges from ordered tasks (fallback when no prereqs/edges).
 *
 * @param {object[]} tasks
 * @returns {ReturnType<typeof normalizeWorkflowEdge>[]}
 */
export function buildWorkflowSpineEdges(tasks) {
  const rows = (Array.isArray(tasks) ? tasks : [])
    .map((task) => toKey(task?.key))
    .filter(Boolean);

  const edges = [];
  for (let index = 0; index < rows.length - 1; index += 1) {
    edges.push({
      key: `edge_${rows[index]}_complete_${rows[index + 1]}`,
      fromTaskKey: rows[index],
      outcomeKey: WORKFLOW_OUTCOME_COMPLETE,
      toTaskKey: rows[index + 1],
    });
  }
  return edges;
}

/**
 * Rewrites Complete edges to follow task list order; keeps custom-outcome edges.
 * Last task Completes to null (ends). Used when the Tasks list is the default spine.
 *
 * @param {object[]} edges - Existing workflow edges.
 * @param {object[]} tasks - Tasks in authoring list order.
 * @returns {ReturnType<typeof normalizeWorkflowEdge>[]}
 */
export function applyWorkflowCompleteSpineFromTaskOrder(edges, tasks) {
  const taskList = Array.isArray(tasks) ? tasks : [];
  const spine = buildWorkflowSpineEdges(taskList);
  const lastKey = toKey(taskList[taskList.length - 1]?.key);
  if (lastKey) {
    spine.push({
      key: `edge_${lastKey}_complete_end`,
      fromTaskKey: lastKey,
      outcomeKey: WORKFLOW_OUTCOME_COMPLETE,
      toTaskKey: null,
    });
  }

  const customEdges = normalizeWorkflowEdges(edges).filter(
    (edge) => edge.outcomeKey !== WORKFLOW_OUTCOME_COMPLETE
  );

  return normalizeWorkflowEdges([...customEdges, ...spine]);
}

/**
 * Migrates legacy prerequisiteTaskKeys into complete-edges.
 *
 * @param {object[]} tasks
 * @returns {ReturnType<typeof normalizeWorkflowEdge>[]}
 */
export function migratePrerequisitesToWorkflowEdges(tasks) {
  const taskList = Array.isArray(tasks) ? tasks : [];
  const taskKeys = new Set(taskList.map((task) => toKey(task?.key)).filter(Boolean));
  const edges = [];
  const seen = new Set();

  taskList.forEach((task) => {
    const toTaskKey = toKey(task?.key);
    if (!toTaskKey) return;

    const prereqs = Array.isArray(task?.prerequisiteTaskKeys)
      ? task.prerequisiteTaskKeys
      : (Array.isArray(task?.prerequisites) ? task.prerequisites : []);

    prereqs.forEach((entry) => {
      const fromTaskKey = toKey(entry);
      if (!fromTaskKey || fromTaskKey === toTaskKey || !taskKeys.has(fromTaskKey)) return;
      const dedupeKey = `${fromTaskKey}\u0001${WORKFLOW_OUTCOME_COMPLETE}`;
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);
      edges.push({
        key: `edge_${fromTaskKey}_complete_${toTaskKey}`,
        fromTaskKey,
        outcomeKey: WORKFLOW_OUTCOME_COMPLETE,
        toTaskKey,
      });
    });
  });

  return edges;
}

/**
 * Ensures a template has edges (migrate prereqs or build spine). Strips prereqs from tasks.
 *
 * @param {object} template
 * @returns {{ tasks: object[], edges: object[] }}
 */
export function ensureWorkflowTemplateGraph(template) {
  const tasks = (Array.isArray(template?.tasks) ? template.tasks : []).map((task) => {
    const key = toKey(task?.key);
    const handledBy = String(task?.handledBy || '').trim().toLowerCase() === MISSION_TASK_HANDLED_BY.BOT
      ? MISSION_TASK_HANDLED_BY.BOT
      : MISSION_TASK_HANDLED_BY.PEOPLE;
    const outcomes = normalizeWorkflowOutcomes(task?.outcomes, { handledBy });
    const canvas = normalizeWorkflowCanvasPosition(task?.canvas);
    const next = {
      ...task,
      key,
      handledBy,
      outcomes,
    };
    if (canvas) next.canvas = canvas;
    else delete next.canvas;
    delete next.prerequisiteTaskKeys;
    delete next.prerequisites;
    return next;
  }).filter((task) => task.key);

  let edges = normalizeWorkflowEdges(template?.edges);
  if (edges.length === 0) {
    const fromPrereqs = migratePrerequisitesToWorkflowEdges(
      Array.isArray(template?.tasks) ? template.tasks : []
    );
    edges = fromPrereqs.length > 0
      ? normalizeWorkflowEdges(fromPrereqs)
      : normalizeWorkflowEdges(buildWorkflowSpineEdges(tasks));
  }

  // Drop edges that reference missing tasks; keep terminal (null) targets.
  const taskKeySet = new Set(tasks.map((task) => task.key));
  edges = edges.filter((edge) => (
    taskKeySet.has(edge.fromTaskKey)
    && (edge.toTaskKey == null || taskKeySet.has(edge.toTaskKey))
  ));

  // Bot edges must be complete-only.
  edges = edges.filter((edge) => {
    const fromTask = tasks.find((task) => task.key === edge.fromTaskKey);
    if (!fromTask) return false;
    if (fromTask.handledBy === MISSION_TASK_HANDLED_BY.BOT) {
      return edge.outcomeKey === WORKFLOW_OUTCOME_COMPLETE;
    }
    return listWorkflowTaskOutcomeKeys(fromTask).includes(edge.outcomeKey);
  });

  return { tasks, edges };
}

/**
 * Assigns simple horizontal spine canvas positions.
 *
 * @param {object[]} tasks
 * @param {object|number} [optionsOrGapX] - Options, or legacy gap (ignored for axis).
 * @param {number} [options.gapX=280]
 * @param {number} [options.startX=80]
 * @param {number} [options.y=140]
 * @param {boolean} [options.force=false] - Overwrite existing canvas positions.
 * @returns {object[]}
 */
export function assignWorkflowSpineCanvas(tasks, optionsOrGapX = {}) {
  const options = (optionsOrGapX && typeof optionsOrGapX === 'object')
    ? optionsOrGapX
    : {
      gapX: Number(optionsOrGapX) || 280,
    };
  const gapX = Number(options.gapX) > 0 ? Number(options.gapX) : 280;
  const startX = Number.isFinite(Number(options.startX)) ? Number(options.startX) : 80;
  const y = Number.isFinite(Number(options.y)) ? Number(options.y) : 140;
  const force = options.force === true;

  return (Array.isArray(tasks) ? tasks : []).map((task, index) => {
    if (!force && normalizeWorkflowCanvasPosition(task?.canvas)) return task;
    return {
      ...task,
      canvas: {
        x: startX + (index * gapX),
        y,
      },
    };
  });
}

/**
 * Validates a workflow graph for designer save.
 *
 * @param {object[]} tasks
 * @param {object[]} edges
 * @returns {{ ok: boolean, errors: string[] }}
 */
export function validateWorkflowGraph(tasks, edges) {
  const errors = [];
  const taskList = Array.isArray(tasks) ? tasks : [];
  const edgeList = normalizeWorkflowEdges(edges);
  const taskKeys = taskList.map((task) => toKey(task?.key)).filter(Boolean);
  const taskKeySet = new Set(taskKeys);

  if (taskKeys.length === 0) {
    return { ok: false, errors: ['Add at least one task.'] };
  }

  const incomingCount = new Map(taskKeys.map((key) => [key, 0]));
  edgeList.forEach((edge) => {
    if (!taskKeySet.has(edge.fromTaskKey)) {
      errors.push('An edge points from a missing task.');
      return;
    }
    if (edge.toTaskKey != null && !taskKeySet.has(edge.toTaskKey)) {
      errors.push('An edge points to a missing task.');
      return;
    }
    const fromTask = taskList.find((task) => toKey(task?.key) === edge.fromTaskKey);
    if (fromTask?.handledBy === MISSION_TASK_HANDLED_BY.BOT
      && edge.outcomeKey !== WORKFLOW_OUTCOME_COMPLETE) {
      errors.push('Bot tasks can only use Complete.');
    }
    if (fromTask && !listWorkflowTaskOutcomeKeys(fromTask).includes(edge.outcomeKey)) {
      errors.push(`Unknown outcome "${edge.outcomeKey}" on ${fromTask.title || edge.fromTaskKey}.`);
    }
    if (edge.toTaskKey != null) {
      incomingCount.set(edge.toTaskKey, (incomingCount.get(edge.toTaskKey) || 0) + 1);
    }
  });

  const starts = taskKeys.filter((key) => (incomingCount.get(key) || 0) === 0);
  if (starts.length === 0) {
    errors.push('Every task has an incoming connection. Keep exactly one Start task.');
  } else if (starts.length > 1) {
    errors.push('Multiple Start tasks found. Connect extras so only one task has no incoming connection.');
  }

  // Reachability from the single start.
  if (starts.length === 1) {
    const startKey = starts[0];
    const adjacency = new Map(taskKeys.map((key) => [key, []]));
    edgeList.forEach((edge) => {
      if (edge.toTaskKey == null) return;
      const list = adjacency.get(edge.fromTaskKey);
      if (list) list.push(edge.toTaskKey);
    });

    const seen = new Set([startKey]);
    const queue = [startKey];
    while (queue.length > 0) {
      const current = queue.shift();
      (adjacency.get(current) || []).forEach((nextKey) => {
        if (seen.has(nextKey)) return;
        seen.add(nextKey);
        queue.push(nextKey);
      });
    }

    const unreachable = taskKeys.filter((key) => !seen.has(key));
    if (unreachable.length > 0) {
      errors.push('Some tasks are not reachable from the Start task.');
    }
  }

  // Cycle check (DFS).
  const visiting = new Set();
  const visited = new Set();
  const adj = new Map(taskKeys.map((key) => [key, []]));
  edgeList.forEach((edge) => {
    if (edge.toTaskKey == null) return;
    adj.get(edge.fromTaskKey)?.push(edge.toTaskKey);
  });

  const visit = (key) => {
    if (visited.has(key)) return false;
    if (visiting.has(key)) return true;
    visiting.add(key);
    const children = adj.get(key) || [];
    for (let index = 0; index < children.length; index += 1) {
      if (visit(children[index])) return true;
    }
    visiting.delete(key);
    visited.add(key);
    return false;
  };

  for (let index = 0; index < taskKeys.length; index += 1) {
    if (visit(taskKeys[index])) {
      errors.push('Playbook contains a cycle.');
      break;
    }
  }

  // Every outcome on every task must have an edge (including terminal null).
  taskList.forEach((task) => {
    const taskKey = toKey(task?.key);
    if (!taskKey) return;
    const requiredOutcomes = listWorkflowTaskOutcomeKeys(task);
    requiredOutcomes.forEach((outcomeKey) => {
      const hasEdge = edgeList.some((edge) => (
        edge.fromTaskKey === taskKey && edge.outcomeKey === outcomeKey
      ));
      if (!hasEdge) {
        const label = outcomeKey === WORKFLOW_OUTCOME_COMPLETE
          ? 'Complete'
          : (normalizeWorkflowOutcomes(task?.outcomes).find((entry) => entry.key === outcomeKey)?.label || outcomeKey);
        errors.push(`Route “${label}” on ${task.title || taskKey} to a task or to End.`);
      }
    });
  });

  const uniqueErrors = Array.from(new Set(errors));
  return { ok: uniqueErrors.length === 0, errors: uniqueErrors };
}

/**
 * Completion outcome on a run step (legacy complete → complete).
 *
 * @param {object|null|undefined} task
 * @returns {string}
 */
export function getWorkflowCompletionOutcomeKey(task) {
  const status = String(task?.status || '').trim();
  if (status !== 'complete') return '';
  const outcome = toOutcomeKey(task?.completionOutcomeKey);
  return outcome || WORKFLOW_OUTCOME_COMPLETE;
}

/**
 * Incoming edges for a task.
 *
 * @param {object[]} edges
 * @param {string} taskKey
 * @returns {object[]}
 */
export function getWorkflowIncomingEdges(edges, taskKey) {
  const normalizedKey = toKey(taskKey);
  if (!normalizedKey) return [];
  return normalizeWorkflowEdges(edges).filter((edge) => edge.toTaskKey === normalizedKey);
}

/**
 * Outgoing edges for a task.
 *
 * @param {object[]} edges
 * @param {string} taskKey
 * @returns {object[]}
 */
export function getWorkflowOutgoingEdges(edges, taskKey) {
  const normalizedKey = toKey(taskKey);
  if (!normalizedKey) return [];
  return normalizeWorkflowEdges(edges).filter((edge) => edge.fromTaskKey === normalizedKey);
}

/**
 * Removes a task from a workflow graph.
 *
 * Incoming edges are reconnected through the deleted task's Complete target when
 * one exists; otherwise they become terminal Ends. Outgoing edges are dropped.
 *
 * @param {object[]} tasks
 * @param {object[]} edges
 * @param {string} taskKey
 * @returns {{ tasks: object[], edges: ReturnType<typeof normalizeWorkflowEdge>[] }}
 */
export function removeWorkflowTaskFromGraph(tasks, edges, taskKey) {
  const normalizedKey = toKey(taskKey);
  const taskList = Array.isArray(tasks) ? tasks : [];
  const edgeList = normalizeWorkflowEdges(edges);

  if (!normalizedKey) {
    return { tasks: taskList, edges: edgeList };
  }

  const nextTasks = taskList.filter((task) => toKey(task?.key) !== normalizedKey);
  if (nextTasks.length === taskList.length) {
    return { tasks: taskList, edges: edgeList };
  }

  const completeOut = edgeList.find((edge) => (
    edge.fromTaskKey === normalizedKey
    && edge.outcomeKey === WORKFLOW_OUTCOME_COMPLETE
  ));
  const reconnectTo = completeOut?.toTaskKey == null
    ? null
    : toKey(completeOut.toTaskKey);

  const nextEdges = edgeList
    .filter((edge) => edge.fromTaskKey !== normalizedKey)
    .map((edge) => {
      if (edge.toTaskKey !== normalizedKey) return edge;
      const toTaskKey = reconnectTo && reconnectTo !== edge.fromTaskKey
        ? reconnectTo
        : null;
      return {
        ...edge,
        key: `edge_${edge.fromTaskKey}_${edge.outcomeKey}_${toTaskKey || 'end'}`,
        toTaskKey,
      };
    });

  return {
    tasks: nextTasks,
    edges: normalizeWorkflowEdges(nextEdges),
  };
}

/**
 * Replaces all outgoing edges for a task with the provided outcome routes.
 * Other tasks' edges are left unchanged. `toTaskKey: null` means the path ends.
 *
 * @param {object[]} edges - Existing workflow edges.
 * @param {string} fromTaskKey - Task whose outgoing routes are being set.
 * @param {Array<{ outcomeKey?: string, toTaskKey?: string|null }>} routes - Desired routes.
 * @returns {ReturnType<typeof normalizeWorkflowEdge>[]}
 */
export function applyWorkflowOutgoingRoutes(edges, fromTaskKey, routes = []) {
  const from = toKey(fromTaskKey);
  if (!from) return normalizeWorkflowEdges(edges);

  const byOutcome = new Map();
  (Array.isArray(routes) ? routes : []).forEach((route) => {
    const outcomeKey = toOutcomeKey(route?.outcomeKey) || WORKFLOW_OUTCOME_COMPLETE;
    const rawTo = route?.toTaskKey;
    const toTaskKey = rawTo == null || rawTo === ''
      ? null
      : toKey(rawTo);
    if (toTaskKey && toTaskKey === from) return;
    byOutcome.set(outcomeKey, {
      key: `edge_${from}_${outcomeKey}_${toTaskKey || 'end'}`,
      fromTaskKey: from,
      outcomeKey,
      toTaskKey,
    });
  });

  const others = normalizeWorkflowEdges(edges).filter((edge) => edge.fromTaskKey !== from);
  return normalizeWorkflowEdges([...others, ...byOutcome.values()]);
}

/**
 * Whether an incoming edge is satisfied by current run state.
 *
 * @param {object} edge
 * @param {Map<string, object>} taskMap
 * @returns {boolean}
 */
export function isWorkflowIncomingEdgeSatisfied(edge, taskMap) {
  const source = taskMap.get(edge.fromTaskKey);
  if (!source) return false;
  if (String(source.status || '').trim() !== 'complete') return false;
  return getWorkflowCompletionOutcomeKey(source) === edge.outcomeKey;
}

/**
 * Whether an incoming edge can never be satisfied (dead branch).
 *
 * @param {object} edge
 * @param {Map<string, object>} taskMap
 * @returns {boolean}
 */
export function isWorkflowIncomingEdgeDead(edge, taskMap) {
  const source = taskMap.get(edge.fromTaskKey);
  if (!source) return true;
  const status = String(source.status || '').trim();
  if (status === 'skipped') return true;
  if (status === 'complete') {
    return getWorkflowCompletionOutcomeKey(source) !== edge.outcomeKey;
  }
  if (status === 'failed') return true;
  return false;
}

/**
 * Creates a draft custom outcome with a unique key.
 *
 * @param {string} label
 * @param {string[]} [existingKeys]
 * @returns {{ key: string, label: string }}
 */
export function createWorkflowOutcomeDraft(label, existingKeys = []) {
  const baseLabel = String(label || '').trim() || 'Outcome';
  let key = toOutcomeKey(baseLabel) || `outcome_${generateKey()}`;
  const existing = new Set(
    (Array.isArray(existingKeys) ? existingKeys : []).map((entry) => toOutcomeKey(entry)).filter(Boolean)
  );
  existing.add(WORKFLOW_OUTCOME_COMPLETE);

  if (!existing.has(key)) {
    return { key, label: baseLabel };
  }

  let suffix = 2;
  while (existing.has(`${key}_${suffix}`)) suffix += 1;
  return { key: `${key}_${suffix}`, label: baseLabel };
}
